import type { AdPlatform } from '@marketing-tool/shared';
import React from 'react';

import { trpc } from '../lib/trpc';

import * as remote from './remote-data';

export type Game = {
    id: string;
    name: string;
    platforms: AdPlatform[];
    defaultLandingPageUrl?: string;
};

export type GamesContext = {
    games: remote.RemoteData<Game[], string>;
    load: () => void;
};

export const Context = React.createContext<GamesContext>({
    games: remote.NotAsked,
    load: () => {
        throw new Error('Provider not initialized');
    },
});

export const Provider = ({ children }: { children: React.ReactNode }) => {
    const games = React.useRef<remote.RemoteData<Game[], string>>(remote.NotAsked);
    const [, setCounter] = React.useState(0);

    const render = React.useCallback(
        (fn?: () => void) => {
            fn?.();
            setCounter((c) => c + 1);
        },
        [setCounter],
    );

    const api = React.useMemo(
        () => ({
            async load() {
                if (remote.isLoading(games.current)) {
                    return; // Already loading, avoid duplicate requests
                }

                render(() => {
                    games.current = remote.Loading;
                });

                try {
                    const data = await trpc.games.list.query();
                    render(() => {
                        games.current = remote.Success(data);
                    });
                } catch (error) {
                    render(() => {
                        games.current = remote.Failure(error instanceof Error ? error.message : String(error));
                    });
                }
            },
        }),
        [render],
    );
    return <Context.Provider value={{ ...api, games: games.current }}>{children}</Context.Provider>;
};

export const useGames = () => {
    const { games, load } = React.useContext(Context);
    React.useEffect(() => {
        if (remote.isNotAsked(games)) {
            load();
        }
    }, []);
    return games;
};

export const useGame = (gameId: string) => {
    const games = useGames();
    return React.useMemo(
        () =>
            remote.matchSome(games, { Success: (games) => games.find((game) => game.id === gameId) }, () => undefined),
        [games, gameId],
    );
};
